import React, { useEffect, useState } from 'react';
import {
  Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, Avatar, useMediaQuery, Badge, IconButton, Tooltip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
  Storage as StorageIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Logout as LogoutIcon,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../../images/logo.png';
import './sidebar.css';
import { menuDefinitions } from '../../commun/Menu';

const Sidebar = ({
  collapsed, onToggleCollapse,
  activeItem,
  setActiveItem,
  mobileOpen,
  handleDrawerToggle,
  drawerWidth = 260
}) => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const navigate = useNavigate();
  const location = useLocation();

  const [contactCount, setContactCount] = useState(0);
  const [permissions, setPermissions] = useState({});
  const userRole = localStorage.getItem('userType')?.toLowerCase();
  const userName = localStorage.getItem('userName') || 'Utilisateur';

  useEffect(() => {
    const fetchPermissions = async () => {
      if (userRole === 'admin') {
        const fullAccess = {};
        menuDefinitions.forEach(m => fullAccess[m.name] = { Accès: true });
        setPermissions(fullAccess);
      } else {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/roles/${userRole}/permissions`);
          setPermissions(res.data || {});
        } catch {
          setPermissions({});
        }
      }
    };

    const fetchContacts = async () => {
      if (userRole === 'admin') {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/admin/contact-messages`);
          setContactCount(res.data.filter(m => !m.lu).length);
        } catch { }
      }
    };

    fetchPermissions();
    fetchContacts();
  }, [userRole]);

  const handleNavClick = (itemName, path) => {
    if (itemName === 'déconnexion') {
      localStorage.clear();
      navigate('/login');
    } else {
      setActiveItem(itemName);
      navigate(path);
      if (isMobile) handleDrawerToggle();
    }
  };

  const getIcon = (name) => {
    const icons = {
      dashboard: <DashboardIcon />,
      entreprises: <BusinessIcon />,
      recruteurs: <PeopleIcon />,
      utilisateurs: <PeopleIcon />,
      missions: <WorkIcon />,
      candidatures: <DescriptionIcon />,
      cvtheque: <StorageIcon />,
      administration: (
        <Badge badgeContent={contactCount > 0 ? contactCount : null} color="error">
          <AdminPanelSettingsIcon />
        </Badge>
      )
    };
    return icons[name] || null;
  };

  const navItems = [
    ...menuDefinitions.map(item => ({
      name: item.name,
      label: item.label,
      icon: getIcon(item.name),
      path: `/${userRole}/${item.name}`
    })),
    {
      name: 'déconnexion',
      label: 'Déconnexion',
      icon: <LogoutIcon />,
      path: '/login'
    }
  ];

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        className: `sidebar-paper ${collapsed ? 'collapsed' : ''}`,
      }}
    >
      <div className="sidebar-paper-inner">
        <div className="sidebar-logo">
          {!collapsed && <img src={logo} alt="Logo" />}
          <IconButton onClick={onToggleCollapse} className="collapse-btn">
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </div>

        <List className="nav-list">
          {navItems.map(item => {
            const isActive = activeItem === item.name;
            const hasAccess =
              userRole === 'admin' ||
              item.name === 'déconnexion' ||
              permissions?.[item.name]?.Accès;

            if (!hasAccess) return null;

            return (
              <Tooltip title={collapsed ? item.label : ''} placement="right" key={item.name}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleNavClick(item.name, item.path)}
                    className={`nav-item-button ${isActive ? 'active' : ''}`}
                  >
                    <ListItemIcon className="nav-item-icon">{item.icon}</ListItemIcon>
                    {!collapsed && (
                      <ListItemText primary={item.label} classes={{ primary: 'nav-item-text' }} />
                    )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}
        </List>

        <Divider />

        <div className="sidebar-footer">
          <Avatar
            src="https://images.unsplash.com/photo-1517365830460-955ce3ccd263"
            alt={userName}
          />
          {!collapsed && (
            <div className="info">
              <span className="info-name">{userName}</span>
              <span className="info-role">{userRole}</span>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default Sidebar;
